import { useMemo } from "react"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import { convertToCubeFilters } from "~/ui-rtk/utils/cube"
import { type TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import useConnect from "~/ui-rtk/components/ui/charts/ChartDataLoader/connect"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"

export function useCampaignOptions() {
  const { depVar, depVarScaler } = useDepVar()

  // Use same date range logic as main dashboard
  const { dateRange } = useMinMaxDateRange()

  const cubeFilters = useMemo(() => {
    const filterRecord: Record<string, string | string[] | undefined | null> = {
      dep_var: depVar,
      dep_var_scaler: depVarScaler,
    }

    const baseFilters = convertToCubeFilters(
      "company_media_metrics_adsets",
      filterRecord,
    )

    return {
      and: [
        ...baseFilters,
        {
          or: [], // Empty or clause to match main dashboard structure
        },
      ],
    } as TCubeFilterOptions
  }, [depVar, depVarScaler])

  // Use useConnect with the new campaign_options_query chart ID
  const { dataSource, isLoading } = useConnect({
    chartId: "campaign_options_query",
    granularity: AggregationType.MONTHLY,
    dateRange: dateRange || [new Date(), new Date()],
    page: 0,
    itemsPerPage: 10000, // Large number to get all campaigns
    enablePagination: false,
    cubeFilters,
  })

  const campaignOptions = useMemo(() => {
    if (!dataSource) return []

    const uniqueCampaigns = new Map<string, string>()

    // Remove duplicates by campaign_id and keep campaign_name
    dataSource.forEach(row => {
      const campaignId = row["company_media_metrics_adsets.campaign_id"]
      const campaignName = row["company_media_metrics_adsets.campaign_name"]

      if (campaignId && campaignName) {
        uniqueCampaigns.set(campaignId as string, campaignName as string)
      }
    })

    // Convert to TMultiSelectOption format
    return Array.from(uniqueCampaigns.entries()).map(([id, name]) => ({
      value: id,
      label: name,
    })) as TMultiSelectOption[]
  }, [dataSource])

  return {
    campaignOptions,
    isLoading,
  }
}

export default useCampaignOptions
